import { ethers } from 'ethers';
import { erc20Abi, bridgeBaseAbi, bridgeCustomAbi } from '../abis';
import { BRIDGE_ADDRESSES, BRIDGE_TOKENS, BASE_CONFIG, SWARM_DEVNET_CONFIG, BRIDGE_FALLBACK_FEE_BPS, BRIDGE_FEE_MODE } from '../constants';

export interface BridgeQuote {
  amountIn: string;
  amountOutEstimated: string;
  feeEstimated: string;
  etaSeconds?: number;
  route?: string;
}

export interface BridgeInitResult {
  success: boolean;
  bridgeTxId?: string;
  transactionHash?: string;
  error?: string;
}

export interface BridgeStatus {
  status: 'pending' | 'completed' | 'failed' | 'unknown';
  bridgeTxId?: string;
  srcTxHash?: string;
  dstTxHash?: string;
  message?: string;
}

export class BridgeService {
  private provider: ethers.BrowserProvider | null = null;
  private signer: ethers.Signer | null = null;

  private async ensureProvider() {
    if (!this.provider && typeof window !== 'undefined' && (window as any).ethereum) {
      this.provider = new ethers.BrowserProvider((window as any).ethereum);
    }
    if (!this.provider) throw new Error('Provider not available');
  }

  private async ensureSigner() {
    await this.ensureProvider();
    if (!this.signer) this.signer = await this.provider!.getSigner();
  }

  private async switchChainIfNeeded(targetChainHex: string): Promise<void> {
    await this.ensureProvider();
    const net = await this.provider!.getNetwork();
    const currentHex = '0x' + net.chainId.toString(16);
    if (currentHex.toLowerCase() === targetChainHex.toLowerCase()) return;
    const ethereum = (window as any).ethereum;
    if (!ethereum) throw new Error('MetaMask not found');
    try {
      await ethereum.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: targetChainHex }] });
    } catch (err: any) {
      if (err?.code === 4902) {
        // add then switch
        const params = targetChainHex.toLowerCase() === SWARM_DEVNET_CONFIG.chainId.toLowerCase()
          ? { chainId: SWARM_DEVNET_CONFIG.chainId, chainName: SWARM_DEVNET_CONFIG.chainName, nativeCurrency: SWARM_DEVNET_CONFIG.nativeCurrency, rpcUrls: [SWARM_DEVNET_CONFIG.rpcUrls[0].replace('wss://', 'https://')], blockExplorerUrls: SWARM_DEVNET_CONFIG.blockExplorerUrls }
          : { chainId: BASE_CONFIG.chainId, chainName: BASE_CONFIG.chainName, nativeCurrency: BASE_CONFIG.nativeCurrency, rpcUrls: BASE_CONFIG.rpcUrls, blockExplorerUrls: BASE_CONFIG.blockExplorerUrls };
        await ethereum.request({ method: 'wallet_addEthereumChain', params: [params] });
      } else {
        throw err;
      }
    }
  }

  private async approveToken(tokenAddress: string, spender: string, amount: string, decimals: number): Promise<string> {
    await this.ensureSigner();
    const token = new ethers.Contract(tokenAddress, erc20Abi, this.signer!);
    const amountWei = ethers.parseUnits(amount, decimals);
    const tx = await token.approve(spender, amountWei);
    const receipt = await this.provider!.waitForTransaction(tx.hash, 1);
    if (!receipt || !receipt.blockNumber) throw new Error('Approval tx not confirmed');
    return tx.hash;
  }

  /**
   * Estimate bridge parameters. Placeholder implementation to be wired to an aggregator.
   */
  async getQuote(amount: string, fromChainId: number, toChainId: number, token: 'SWM' | 'WETH'): Promise<BridgeQuote> {
    const decimals = token === 'SWM' ? 9 : 18;
    const amountWei = ethers.parseUnits(amount || '0', decimals);
    let feeWei: bigint | null = null;

    if (BRIDGE_FEE_MODE === 'onchain') {
      try {
        await this.ensureSigner();
        const net = await this.provider!.getNetwork();
        const currentFrom = Number(net.chainId);
        if (currentFrom === fromChainId) {
          if (fromChainId === 1322837) {
            const bridge = new ethers.Contract(BRIDGE_ADDRESSES.CUSTOM_1322837, bridgeCustomAbi, this.signer!);
            if (token === 'WETH') feeWei = await bridge.bridgeFeeWETH();
            if (token === 'SWM') feeWei = await bridge.bridgeFeeCredit();
          } else if (fromChainId === 8453) {
            // Base not supported for current token selection; skip on-chain fee
          }
        }
      } catch {
        feeWei = null;
      }
    }

    if (feeWei === null) {
      // fallback bps
      feeWei = (amountWei * BigInt(BRIDGE_FALLBACK_FEE_BPS)) / BigInt(10000);
    }

    if (feeWei > amountWei) feeWei = amountWei;
    const outWei = amountWei - feeWei;
    return {
      amountIn: amount,
      amountOutEstimated: ethers.formatUnits(outWei, decimals),
      feeEstimated: ethers.formatUnits(feeWei, decimals),
      etaSeconds: 300,
      route: `bridge:${fromChainId}->${toChainId}`,
    };
  }

  /**
   * Initiate a bridge for a specific token symbol.
   * token: 'SWM' | 'USDC' | 'WETH' | 'CREDIT'
   */
  async initiateBridgeToken(token: 'SWM' | 'WETH', amount: string): Promise<BridgeInitResult> {
    try {
      if (!amount || Number(amount) <= 0) return { success: false, error: 'Invalid amount' };
      await this.ensureSigner();
      await this.signer!.getAddress();
      const net = await this.provider!.getNetwork();
      const chainId = Number(net.chainId);

      // Custom network 1322837
      if (chainId === 1322837) {
        const bridgeAddr = BRIDGE_ADDRESSES.CUSTOM_1322837;
        const bridge = new ethers.Contract(bridgeAddr, bridgeCustomAbi, this.signer!);
        if (token === 'WETH') {
          const tokenAddr = BRIDGE_TOKENS.CUSTOM.WETH;
          if (!tokenAddr) return { success: false, error: 'WETH token address not configured for Custom' };
          // Pre-validate min and fee to avoid opaque estimateGas reverts
          let fee = BigInt(0);
          let min = BigInt(0);
          try {
            fee = await bridge.bridgeFeeWETH();
            min = await bridge.minBridgeAmountWETH();
          } catch {}
          const amtWei = ethers.parseUnits(amount, 18);
          if (amtWei < min) return { success: false, error: `Amount below minimum (${ethers.formatUnits(min, 18)})` };
          if (amtWei <= fee) return { success: false, error: `Amount must exceed fee (${ethers.formatUnits(fee, 18)})` };
          await this.approveToken(tokenAddr, bridgeAddr, amount, 18);
          const tx = await bridge.depositWETH(amtWei);
          const receipt = await this.provider!.waitForTransaction(tx.hash, 1);
          if (!receipt || !receipt.blockNumber) throw new Error('Bridge tx not confirmed');
          return { success: true, transactionHash: tx.hash };
        }
        if (token === 'SWM') {
          const tokenAddr = BRIDGE_TOKENS.CUSTOM.SWM;
          if (!tokenAddr) return { success: false, error: 'SWM token address not configured for Custom' };
          let fee = BigInt(0);
          let min = BigInt(0);
          try {
            fee = await bridge.bridgeFeeCredit();
            min = await bridge.minBridgeAmountCredit();
          } catch {}
          const amtWei = ethers.parseUnits(amount, 9);
          if (amtWei < min) return { success: false, error: `Amount below minimum (${ethers.formatUnits(min, 9)})` };
          if (amtWei <= fee) return { success: false, error: `Amount must exceed fee (${ethers.formatUnits(fee, 9)})` };
          await this.approveToken(tokenAddr, bridgeAddr, amount, 9);
          const tx = await bridge.depositCredit(amtWei);
          const receipt = await this.provider!.waitForTransaction(tx.hash, 1);
          if (!receipt || !receipt.blockNumber) throw new Error('Bridge tx not confirmed');
          return { success: true, transactionHash: tx.hash };
        }
        return { success: false, error: 'On Custom network, supported tokens: WETH or SWM' };
      }

      // Base mainnet 8453
      if (chainId === 8453) {
        return { success: false, error: 'Base bridging is temporarily disabled in UI' };
      }

      return { success: false, error: `Unsupported network ${chainId}` };
    } catch (error: any) {
      return { success: false, error: error?.message || 'Bridge failed' };
    }
  }

  /**
   * Convenience: ensure we are on desired chain then initiate token bridge.
   */
  async initiateBridgeWithChain(token: 'SWM' | 'WETH', amount: string, targetChain: 'CUSTOM' | 'BASE'): Promise<BridgeInitResult> {
    const targetHex = targetChain === 'CUSTOM' ? SWARM_DEVNET_CONFIG.chainId : BASE_CONFIG.chainId;
    try {
      await this.switchChainIfNeeded(targetHex);
    } catch (e: any) {
      return { success: false, error: e?.message || 'Network switch failed' };
    }
    return this.initiateBridgeToken(token, amount);
  }

  /**
   * Poll bridge status. Placeholder that returns completed after a delay on subsequent calls.
   */
  async getStatus(bridgeTxId: string): Promise<BridgeStatus> {
    if (!bridgeTxId) return { status: 'unknown', message: 'No bridge tx id' };
    // Simple deterministic status flip based on timestamp embedded in stub id
    const ts = Number(bridgeTxId.split('-')[1] || 0);
    const elapsed = Date.now() - ts;
    if (elapsed < 10000) return { status: 'pending', bridgeTxId };
    return { status: 'completed', bridgeTxId };
  }

  /**
   * Suggest bridge amount to cover a balance shortfall plus buffer.
   */
  suggestAmountForShortfall(shortfall: string, bufferPercent: number = 25): string {
    try {
      const wei = ethers.parseUnits(shortfall || '0', 9);
      const withBuffer = wei + (wei * BigInt(Math.max(0, bufferPercent))) / BigInt(100);
      return ethers.formatUnits(withBuffer, 9);
    } catch {
      return shortfall || '0';
    }
  }
}

export const bridgeService = new BridgeService();


